import { apiRequest } from '@/lib/api-request';

export type Priority = 'urgent' | 'today' | 'watch';
export type ItemStatus = 'open' | 'resolved' | 'dismissed';
export type StepStatus = 'proposed' | 'approved' | 'running' | 'succeeded' | 'failed' | 'skipped';

export interface CommentEvidence {
  id: string;
  at: string;
  by: string | null;
  author: string | null;
  text: string;
}

export interface Step {
  id: string;
  seq: number;
  action_id: string;
  label: string;
  effects: string | null;
  status: StepStatus;
  params: { body?: string } & Record<string, unknown>;
  editable: boolean;
  result: { comment_id?: string; entity_type?: string; entity_id?: string; posted_at?: string } | null;
  error: string | null;
  started_at: string | null;
  finished_at: string | null;
}

export interface Proposal {
  id: string;
  version: number;
  status: 'proposed' | 'superseded' | 'approved' | 'executed' | 'partial' | 'failed' | 'dismissed';
  source: 'rules' | 'ai';
  summary: string;
  instruction: string | null;
  created_at: string;
  steps: Step[];
  blocked: 'follow_up_limit' | null;
}

export interface HistoryEvent {
  id: string;
  event_type: string;
  at: string;
  actor: string | null;
  data: Record<string, any>;
  proposal_id: string | null;
}

export interface Situation {
  id: string;
  kind: 'vendor_comment_unanswered';
  title: string;
  status: ItemStatus;
  priority: Priority;
  resolved_reason: string | null;
  resolved_at: string | null;
  dismissed_at: string | null;
  dismissed_reason: string | null;
  project_id: string | null;
  project_name: string | null;
  entity: { type: string; id: string; label: string };
  vendor: { id: string; name: string };
  waiting_since: string;
  hours_waiting: number;
  first_seen_at: string;
  last_seen_at: string;
  follow_up_count: number;
  evidence: {
    root_comment: CommentEvidence | null;
    latest_comment: CommentEvidence | null;
    threshold_hours: number;
    follow_up_limit: number;
  };
  proposal: Proposal | null;
  history?: HistoryEvent[];
}

export interface ReviewRun {
  id: string;
  trigger: 'manual' | 'scheduled';
  status: 'running' | 'succeeded' | 'partial' | 'failed';
  started_at: string;
  finished_at: string | null;
  stats: Record<string, number> | null;
  error: string | null;
}

export interface ActivityEntry {
  id: string;
  item_id: string;
  event_type: string;
  at: string;
  actor: string | null;
  data: Record<string, any>;
  entity_label: string | null;
  project_name: string | null;
}

export interface CommandCenterData {
  items: Situation[];
  handled: Situation[];
  activity: ActivityEntry[];
  counts: Record<Priority, number>;
  last_run: ReviewRun | null;
  running: boolean;
  cooldown_seconds: number;
  settings: { followup_after_hours: number; follow_up_limit: number };
  time_zone: string | null;
}

export const fetchCommandCenter = (projectId: string | undefined, signal?: AbortSignal) =>
  apiRequest<CommandCenterData>(`api/command-center${projectId ? `?project_id=${encodeURIComponent(projectId)}` : ''}`, { signal });
export const runReview = () => apiRequest<{ run: ReviewRun; joined: boolean }>('api/command-center/review', { method: 'POST', body: {} });
export const fetchSituation = (id: string) => apiRequest<Situation>(`api/command-center/items/${id}`);
export const dismissSituation = (id: string, reason?: string) => apiRequest<Situation>(`api/command-center/items/${id}/dismiss`, { method: 'POST', body: { reason } });
export const updateSuggestion = (id: string, instruction: string, expectedVersion: number) =>
  apiRequest<{ clarification: string | null; item: Situation; replaced: { version: number; summary: string; new_version: number } | null }>(
    `api/command-center/items/${id}/update-suggestion`, { method: 'POST', body: { instruction, expected_version: expectedVersion } });
export const approveProposal = (proposalId: string, version: number, steps: Array<{ id: string; body: string }>) =>
  apiRequest<Situation>(`api/command-center/proposals/${proposalId}/approve`, { method: 'POST', body: { version, steps } });
export const retryStep = (actionId: string) => apiRequest<Situation>(`api/command-center/actions/${actionId}/retry`, { method: 'POST', body: {} });
